//
import React, { useLayoutEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import type { DialogContentProps } from '@mui/material/DialogContent';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import ErrorBoundary from 'components/ErrorBoundary';

export type StdDialogContentProps = Omit<DialogContentProps, 'sx'> & {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  dialogId?: string;
  sharedcomponentid?: string;
  hideScroll?: boolean;
};

/**
 * StdDialogContent should be used instead of the MUI DialogContent. StdDialogContent is a thin wrapper whose primary purpose
 * is to add scrolling support if content is larger than height constraint assigned by parent. In the case where scrollable
 * content is available, a fixed divider will automatically be added at the bottom of the content.
 *
 * This component is meant to be used with StdDialog.
 */
const StdDialogContent: React.FC<StdDialogContentProps> = ({ children, sx: ferrySx, hideScroll, ...other }) => {
  const theme = useTheme();

  const [requiresScrolling, setRequiresScrolling] = useState(false);
  const [scrollbarGutterPx, setScrollbarGutterPx] = useState(0);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentMeasureRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (hideScroll) {
      const scrollEl = scrollRef.current;
      if (!scrollEl || typeof ResizeObserver === 'undefined') {
        return;
      }

      /**
       * Scrollbars often appear when inner content grows (scrollHeight > clientHeight) without changing the
       * scroll container's border box — so ResizeObserver on the scroll pane alone may never refire.
       * Observing the inner content wrapper plus a second rAF lets us measure gutter after the bar paints.
       */
      const measure = (): void => {
        const needsScrolling = scrollEl.scrollHeight > scrollEl.clientHeight;
        setRequiresScrolling((prev) => (prev !== needsScrolling ? needsScrolling : prev));
        const gutter = scrollEl.offsetWidth - scrollEl.clientWidth;
        setScrollbarGutterPx((prev) => (prev !== gutter ? gutter : prev));
      };

      const scheduleMeasure = (): void => {
        requestAnimationFrame(() => {
          requestAnimationFrame(measure);
        });
      };

      const observer = new ResizeObserver(scheduleMeasure);
      observer.observe(scrollEl);
      const contentEl = contentMeasureRef.current;
      if (contentEl) {
        observer.observe(contentEl);
      }
      scheduleMeasure();

      return () => {
        observer.disconnect();
      };
    }

    const { current } = contentRef;
    if (!current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!current) {
          return;
        }
        const needsScrolling = current.scrollHeight > current.clientHeight;
        setRequiresScrolling((prev) => (prev !== needsScrolling ? needsScrolling : prev));
      });
    });
    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hideScroll]);

  if (hideScroll) {
    return (
      <DialogContent
        sx={{
          boxSizing: 'border-box',
          borderBottom: `1px solid transparent`,
          borderBottomColor: requiresScrolling ? theme.palette.divider : 'transparent',
          ...ferrySx,
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'hidden',
        }}
        {...other}
      >
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            overflowY: 'auto',
            boxSizing: 'border-box',
            marginRight: scrollbarGutterPx > 0 ? -scrollbarGutterPx : 0,
            paddingRight: scrollbarGutterPx > 0 ? scrollbarGutterPx : 0,
          }}
        >
          <ErrorBoundary>
            <Box ref={contentMeasureRef} sx={{ display: 'flex', flexDirection: 'column' }}>
              {children}
            </Box>
          </ErrorBoundary>
        </Box>
      </DialogContent>
    );
  }

  return (
    <DialogContent
      ref={contentRef}
      sx={{
        boxSizing: 'border-box',
        borderBottom: `1px solid transparent`,
        borderBottomColor: requiresScrolling ? theme.palette.divider : 'transparent',
        ...ferrySx,
      }}
      {...other}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </DialogContent>
  );
};

export default StdDialogContent;
